import { Location } from '@angular/common';
import { computed, inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { OKTA_AUTH, OktaAuthStateService } from '@okta/okta-angular';
import { AuthState } from '@okta/okta-auth-js';
import { filter, firstValueFrom, map, tap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class TokenProviderService {
  private oktaStateService = inject(OktaAuthStateService);
  private location = inject(Location);
  private oktaAuth = inject(OKTA_AUTH);

  oktaState$ = this.oktaStateService.authState$.pipe(
    filter((authState: AuthState) => !!authState),
  );

  // A promise that resolves true/false after the Okta Auth state is initialized.
  isAuthenticated = () =>
    firstValueFrom(
      this.oktaState$.pipe(
        tap(async () => {
          if (this.location.path().startsWith('/login/callback'))
            await this.oktaAuth.handleRedirect();
        }),
        filter((state) => {
          return (
            state.isAuthenticated ||
            (!!state &&
              !!state['initialized'] &&
              !this.location.path().startsWith('/login/callback'))
          );
        }),
        map((state) => state.isAuthenticated as boolean),
      ),
    );

  // Converts the Okta Auth state to a signal for use in our reactive application.
  oktaState = toSignal(this.oktaState$, { requireSync: true });

  readonly idToken = computed(() => this.oktaState().idToken?.idToken);
  readonly rawToken = computed(() => this.oktaState().idToken);
  readonly accessToken = computed(() => this.oktaState().accessToken);
}
